import React from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import { useSupplierEnquiry, useSupplierEnquiryRfqLinkStatus } from './useData';
import { moduleConfig } from './supplierEnquiriesModuleConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import {
  Accordion,
  AccordionItem,
  Button,
  Callout,
  Card,
  CountChip,
  CheckCircleIcon,
  ChevronLeftIcon,
  ClockIcon,
  CopyIcon,
  DocumentTextIcon,
  InfoIcon,
  LockIcon,
  NumberInput,
  TableIcon,
  TextInput,
  TrashIcon,
} from '../../../components/ui';
import {
  createSupplierEnquiryRfqLink,
  downloadSupplierEnquiryRfqTemplate,
  importSupplierEnquiryRfqExcel,
  revokeSupplierEnquiryRfqLink,
} from '../../../services/mutations';
import { supplyBlockedActionMessage } from '../blockingMessages';

const formatEnquiryItemCell = (line: {
  itemVcode?: string | null;
  itemDescr?: string | null;
}) => {
  const v = (line.itemVcode ?? '').trim();
  const n = (line.itemDescr ?? '').trim();
  return [v, n].filter(Boolean).join(' - ');
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numericId = id ? Number(id) : NaN;
  const { item, isLoading, isError } = useSupplierEnquiry(Number.isFinite(numericId) ? numericId : undefined);
  const { status: rfqLinkStatus, mutate: mutateRfqLink } = useSupplierEnquiryRfqLinkStatus(
    Number.isFinite(numericId) ? numericId : undefined,
  );
  const { colors } = useTheme();
  const goBack = useNavigateBack(moduleConfig.module.route.list);
  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');

  const [rfqPin, setRfqPin] = React.useState('');
  const [rfqExpiresDays, setRfqExpiresDays] = React.useState(14);
  /** Full supplier URL from the last successful generate (session-only; not returned by API on reload). */
  const [rfqSupplierLinkUrl, setRfqSupplierLinkUrl] = React.useState('');
  const [rfqLinkBusy, setRfqLinkBusy] = React.useState(false);
  const [importBusy, setImportBusy] = React.useState(false);
  const importInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    setRfqSupplierLinkUrl('');
  }, [numericId]);

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading supplier enquiry…</p>
      </div>
    );
  }

  if (isError || !item) {
    return (
      <Card variant="outlined" className={cn('p-6 sm:p-8', colors.border, 'max-w-md')}>
        <p className={cn('text-sm', colors.textSecondary)}>This supplier enquiry was not found or you no longer have access.</p>
        <Button type="button" variant="ghost" className="mt-4" onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back to list
        </Button>
      </Card>
    );
  }

  const lineCount = (item.lines || []).length;
  const supplierLabel = item.supplier
    ? item.supplier.code
      ? `${item.supplier.name} (${item.supplier.code})`
      : item.supplier.name
    : `Supplier #${item.supplierId}`;

  const rfqExpiresMs =
    rfqLinkStatus?.expiresAt != null ? new Date(rfqLinkStatus.expiresAt).getTime() : null;
  const linkExpired = Boolean(
    rfqLinkStatus?.configured && rfqExpiresMs != null && rfqExpiresMs < Date.now(),
  );
  const linkSubmitted = Boolean(rfqLinkStatus?.submittedAt);
  const linkActive = Boolean(rfqLinkStatus?.configured && !linkSubmitted && !linkExpired);
  const editBlockedByQuotation = (item._count?.quotations ?? 0) > 0;

  return (
    <div className="space-y-6">
      <Card
        variant="outlined"
        className={cn('overflow-hidden border shadow-sm', colors.border)}
      >
        <div
          className={cn(
            'flex flex-col gap-4 border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn('-ml-2 gap-1.5 self-start font-medium', colors.text)}
                onClick={goBack}
              >
                <ChevronLeftIcon className="h-4 w-4 opacity-80" />
                Back
              </Button>
            </div>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {editBlockedByQuotation ? (
                <button
                  type="button"
                  disabled
                  title={supplyBlockedActionMessage('ENQ_EDIT_LINKED_QUOTATION')}
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 cursor-not-allowed items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold opacity-60',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </button>
              ) : (
                <Link
                  to={moduleConfig.module.route.edit(item.id)}
                  className={cn(
                    'inline-flex h-9 min-h-[2.25rem] shrink-0 items-center justify-center gap-2 rounded-lg px-4 text-sm font-semibold shadow-sm transition-all hover:opacity-95',
                    colors.primary,
                    colors.primaryText,
                  )}
                >
                  Edit
                </Link>
              )}
            </div>
          </div>
        </div>
      </Card>
      {editBlockedByQuotation ? (
        <Callout variant="danger" title="Cannot edit RFQ">{supplyBlockedActionMessage('ENQ_EDIT_LINKED_QUOTATION')}</Callout>
      ) : null}

      <Card variant="outlined" className={cn('p-4 shadow-sm sm:p-5', colors.border)}>
        <Accordion className="space-y-3">
          <AccordionItem
            title="RFQ details"
            description="Enquiry, supplier, linked case, and requisition."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0 ">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm md:grid-cols-12">
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Enquiry date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.enqDate)}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>{item.ref || ''}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supplier</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>{supplierLabel}</dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Supply case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.supplyCase?.id ? (
                      <Link
                        to={`/supplies/supply-cases/${item.supplyCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.supplyCase.ref || `Case #${item.supplyCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-3">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Requisition</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.requisition?.id ? (
                      <Link
                        to={`/supplies/requisitions/${item.requisition.id}`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.requisition.ref || `Requisition #${item.requisition.id}`}
                      </Link>
                    ) : item.requisitionId ? (
                      <span className="tabular-nums">#{item.requisitionId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0 md:col-span-6 md:col-start-1">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn('mt-1 min-h-[1.25rem] max-w-3xl whitespace-pre-wrap break-words', colors.text)}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Supplier response"
            description="Excel round-trip or a secure one-time web link with PIN."
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              rfqLinkStatus?.configured ? (
                <span
                  className={cn(
                    'mr-1 inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
                    colors.border,
                    linkSubmitted &&
                      'border-emerald-400/50 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
                    !linkSubmitted &&
                      linkExpired &&
                      'border-amber-400/50 bg-amber-500/10 text-amber-900 dark:text-amber-200',
                    linkActive && 'border-violet-400/50 bg-violet-500/10 text-violet-900 dark:text-violet-200',
                    !linkSubmitted && !linkExpired && !linkActive && colors.textSecondary,
                  )}
                >
                  {linkSubmitted ? (
                    <>
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      Submitted
                    </>
                  ) : linkExpired ? (
                    <>
                      <ClockIcon className="h-3.5 w-3.5" />
                      Expired
                    </>
                  ) : linkActive ? (
                    <>
                      <LockIcon className="h-3.5 w-3.5" />
                      Link live
                    </>
                  ) : (
                    'Link'
                  )}
                </span>
              ) : null
            }
          >
            <div className="mt-2 space-y-5 text-sm">
              <div
                className={cn(
                  'flex gap-3 rounded-xl border px-4 py-3.5',
                  colors.border,
                  'bg-gradient-to-br from-indigo-500/[0.07] to-violet-500/[0.06] dark:from-indigo-500/10 dark:to-violet-500/10',
                )}
              >
                <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600 opacity-90 dark:text-indigo-400" />
                <div className="min-w-0 space-y-1">
                  <p className={cn('font-medium', colors.text)}>How suppliers return prices</p>
                  <p className={cn('leading-relaxed', colors.textSecondary)}>
                    Use <span className="font-medium text-emerald-700 dark:text-emerald-300">Excel</span> for email-based
                    workflows, or a <span className="font-medium text-violet-700 dark:text-violet-300">secure link</span>{' '}
                    for in-browser entry. Share the PIN separately from the URL. After a successful web submit, the link
                    stops accepting new responses.
                  </p>
                </div>
              </div>

              <div className="grid gap-5 lg:grid-cols-2 lg:items-stretch">
                {/* Excel */}
                <div
                  className={cn(
                    'flex min-h-full flex-col overflow-hidden rounded-xl border shadow-sm',
                    colors.border,
                  )}
                >
                  <div
                    className={cn(
                      'flex items-start gap-3 border-b px-4 py-3.5 sm:items-center',
                      colors.border,
                      'bg-gradient-to-r from-emerald-500/[0.12] via-emerald-500/[0.06] to-transparent dark:from-emerald-500/15',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border shadow-sm',
                        colors.border,
                        'bg-white/90 dark:bg-slate-900/80',
                      )}
                    >
                      <TableIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </span>
                    <div className="min-w-0">
                      <h4 className={cn('text-base font-semibold leading-tight', colors.text)}>Excel</h4>
                      <p className={cn('mt-0.5 text-xs leading-snug', colors.textSecondary)}>
                        Download the template, supplier fills prices, you import the file here.
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col gap-4 p-4">
                    <ol className={cn('list-none space-y-2.5 border-l-2 pl-4', 'border-emerald-400/40')}>
                      <li className="relative">
                        <span
                          className={cn(
                            'absolute -left-[21px] top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold',
                            colors.border,
                            colors.surface,
                            colors.textSecondary,
                          )}
                        >
                          1
                        </span>
                        <span className={cn(colors.text)}>Download the RFQ spreadsheet.</span>
                      </li>
                      <li className="relative">
                        <span
                          className={cn(
                            'absolute -left-[21px] top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold',
                            colors.border,
                            colors.surface,
                            colors.textSecondary,
                          )}
                        >
                          2
                        </span>
                        <span className={cn(colors.text)}>
                          Supplier completes Unit price / lead time —{' '}
                          <span className={cn('font-medium', colors.textSecondary)}>do not change Enquiry line IDs</span>.
                        </span>
                      </li>
                      <li className="relative">
                        <span
                          className={cn(
                            'absolute -left-[21px] top-1 flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-bold',
                            colors.border,
                            colors.surface,
                            colors.textSecondary,
                          )}
                        >
                          3
                        </span>
                        <span className={cn(colors.text)}>Import the returned file to create the quotation.</span>
                      </li>
                    </ol>
                    <div className="mt-auto flex flex-col gap-2 border-t pt-4 sm:flex-row sm:flex-wrap">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="justify-center sm:justify-start"
                        disabled={rfqLinkBusy}
                        onClick={async () => {
                          setRfqLinkBusy(true);
                          try {
                            const { usedFallback } = await downloadSupplierEnquiryRfqTemplate(item.id);
                            toast.success('Template downloaded.');
                            if (usedFallback) {
                              toast(
                                'Using the built-in RFQ layout. Activate a mapped RFQ template in admin for your branded Excel.',
                                { duration: 5000 },
                              );
                            }
                          } catch {
                            toast.error('Could not download template.');
                          } finally {
                            setRfqLinkBusy(false);
                          }
                        }}
                      >
                        <DocumentTextIcon className="h-4 w-4 opacity-90" />
                        Download template
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="justify-center sm:justify-start"
                        disabled={importBusy}
                        onClick={() => importInputRef.current?.click()}
                      >
                        <TableIcon className="h-4 w-4 opacity-90" />
                        Import completed file
                      </Button>
                      <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f) return;
                          setImportBusy(true);
                          try {
                            await importSupplierEnquiryRfqExcel(item.id, f);
                            toast.success('Quotation imported from Excel.');
                          } catch {
                            /* toast from api interceptor */
                          } finally {
                            setImportBusy(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Secure link */}
                <div
                  className={cn(
                    'flex min-h-full flex-col overflow-hidden rounded-xl border shadow-sm',
                    colors.border,
                  )}
                >
                  <div
                    className={cn(
                      'flex items-start gap-3 border-b px-4 py-3.5 sm:items-center',
                      colors.border,
                      'bg-gradient-to-r from-violet-500/[0.14] via-violet-500/[0.06] to-transparent dark:from-violet-500/18',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border shadow-sm',
                        colors.border,
                        'bg-white/90 dark:bg-slate-900/80',
                      )}
                    >
                      <LockIcon className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h4 className={cn('text-base font-semibold leading-tight', colors.text)}>Secure web link</h4>
                      <p className={cn('mt-0.5 text-xs leading-snug', colors.textSecondary)}>
                        PIN-protected page — generate a URL, share it with the PIN out of band.
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col gap-4 p-4">
                    {rfqLinkStatus?.configured ? (
                      <div
                        className={cn(
                          'grid gap-3 rounded-lg border px-3 py-3 sm:grid-cols-2',
                          colors.border,
                          colors.surfaceHover,
                        )}
                      >
                        <div>
                          <dt className={cn('flex items-center gap-1.5 text-xs font-medium', colors.textSecondary)}>
                            <ClockIcon className="h-3.5 w-3.5 opacity-80" />
                            Expires
                          </dt>
                          <dd className={cn('mt-1 tabular-nums font-medium', colors.text)}>
                            {rfqLinkStatus.expiresAt
                              ? formatDateGlobal(rfqLinkStatus.expiresAt.slice(0, 10))
                              : '—'}
                          </dd>
                        </div>
                        <div>
                          <dt className={cn('flex items-center gap-1.5 text-xs font-medium', colors.textSecondary)}>
                            <CheckCircleIcon className="h-3.5 w-3.5 opacity-80" />
                            Web response
                          </dt>
                          <dd className={cn('mt-1 font-medium', colors.text)}>
                            {rfqLinkStatus.submittedAt
                              ? formatDateGlobal(rfqLinkStatus.submittedAt.slice(0, 10))
                              : 'Awaiting'}
                          </dd>
                        </div>
                      </div>
                    ) : (
                      <p
                        className={cn(
                          'rounded-lg border border-dashed px-3 py-3 text-center text-sm',
                          colors.border,
                          colors.textSecondary,
                        )}
                      >
                        No link yet — set a PIN and validity below to create one.
                      </p>
                    )}

                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="grid gap-1.5">
                        <label className={cn('text-xs font-medium', colors.textSecondary)}>Supplier PIN (min 6)</label>
                        <TextInput
                          type="password"
                          value={rfqPin}
                          onChange={(e) => setRfqPin(e.target.value)}
                          autoComplete="new-password"
                          placeholder="••••••"
                          size="sm"
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <label className={cn('text-xs font-medium', colors.textSecondary)}>Valid for (days)</label>
                        <NumberInput
                          value={rfqExpiresDays}
                          onChange={(e) => setRfqExpiresDays(e.target.value != null ? e.target.value : 14)}
                          min={1}
                          max={365}
                          size="sm"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="justify-center sm:justify-start"
                        disabled={rfqLinkBusy || rfqPin.trim().length < 6}
                        onClick={async () => {
                          setRfqLinkBusy(true);
                          try {
                            const res = await createSupplierEnquiryRfqLink(item.id, {
                              pin: rfqPin.trim(),
                              expiresInDays: rfqExpiresDays,
                            });
                            const href = res.fullUrl || `${window.location.origin}${res.urlPath}`;
                            setRfqSupplierLinkUrl(href);
                            await navigator.clipboard.writeText(href);
                            setRfqPin('');
                            await mutateRfqLink();
                            toast.success(
                              'RFQ link copied to clipboard. Send the link and PIN to the supplier through separate channels.',
                            );
                          } catch {
                            /* interceptor toast */
                          } finally {
                            setRfqLinkBusy(false);
                          }
                        }}
                      >
                        <CopyIcon className="h-4 w-4 opacity-90" />
                        Generate link & copy URL
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="justify-center text-red-600 hover:bg-red-500/10 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 sm:justify-start"
                        disabled={rfqLinkBusy || !rfqLinkStatus?.configured}
                        onClick={async () => {
                          setRfqLinkBusy(true);
                          try {
                            await revokeSupplierEnquiryRfqLink(item.id);
                            setRfqSupplierLinkUrl('');
                            await mutateRfqLink();
                            toast.success('Public RFQ link revoked.');
                          } catch {
                            /* toast */
                          } finally {
                            setRfqLinkBusy(false);
                          }
                        }}
                      >
                        <TrashIcon className="h-4 w-4 opacity-90" />
                        Revoke link
                      </Button>
                    </div>

                    {rfqSupplierLinkUrl ? (
                      <div className="grid gap-1.5 rounded-lg border border-violet-400/25 bg-violet-500/[0.04] px-3 py-3 dark:bg-violet-500/[0.07]">
                        <label className={cn('text-xs font-medium', colors.textSecondary)}>
                          Supplier RFQ link — copy and send (PIN separately)
                        </label>
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
                          <div className="min-w-0 flex-1">
                            <TextInput
                              readOnly
                              value={rfqSupplierLinkUrl}
                              onChange={() => {}}
                              className="font-mono text-xs"
                              size="sm"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="shrink-0 sm:w-auto"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(rfqSupplierLinkUrl);
                                toast.success('Link copied.');
                              } catch {
                                toast.error('Could not copy.');
                              }
                            }}
                          >
                            <CopyIcon className="h-4 w-4 opacity-90" />
                            Copy link
                          </Button>
                        </div>
                        <p className={cn('text-[11px] leading-snug', colors.textSecondary)}>
                          Shown only on this screen after you generate or rotate the link. Reloading the page clears it —
                          use Copy if you still need the URL.
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </AccordionItem>

          <AccordionItem
            title="Line items"
            description="Item codes, quantities, and line notes from this RFQ."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
            headerAction={
              lineCount > 0 ? (
                <CountChip className="mr-1.5">
                  {lineCount} line{lineCount === 1 ? '' : 's'}
                </CountChip>
              ) : null
            }
          >
            <div className="-mx-1 overflow-x-auto sm:mx-0 sm:px-0">
              <table className="w-full min-w-full border-collapse text-left text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs font-semibold uppercase tracking-wider',
                      colors.border,
                      'bg-slate-50/95 dark:bg-slate-800/50',
                    )}
                  >
                    <th className="w-10 py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0">#</th>
                    <th className="w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3">Level 1</th>
                    <th className="min-w-[12rem] py-2.5 pl-0 pr-3 sm:min-w-[16rem]">Item</th>
                    <th className="w-24 min-w-[5.5rem] py-2.5 pl-0 pr-3">Unit</th>
                    <th className="w-28 min-w-[6.5rem] py-2.5 pl-0 pr-3">Req. qty</th>
                    <th className="w-20 min-w-[4.5rem] py-2.5 pl-0 pr-3">Qty</th>
                    <th className="min-w-[8rem] py-2.5 pl-0 pr-0 sm:pr-0">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {(item.lines || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className={cn('py-8 text-center text-sm', colors.textSecondary)}>
                        No line items on this supplier enquiry.
                      </td>
                    </tr>
                  ) : (
                    (item.lines || []).map((line, lineIndex) => (
                      <tr
                        key={line.id ?? `${line.itemId}-${line.itemDescr}`}
                        className={cn(
                          'border-b text-left transition-colors last:border-0',
                          colors.border,
                          'hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                        )}
                      >
                        <td className={cn('py-2.5 pl-1 pr-2 text-left tabular-nums sm:pl-0', colors.textSecondary)}>{lineIndex + 1}</td>
                        <td className={cn('w-[10rem] min-w-[8rem] py-2.5 pl-0 pr-3 align-top', colors.text)}>
                          {(line.item?.level1 ?? '').trim() || ''}
                        </td>
                        <td className={cn('min-w-[12rem] py-2.5 pl-0 pr-3 align-top sm:min-w-[16rem]', colors.text)}>
                          {formatEnquiryItemCell(line)}
                        </td>
                        <td className={cn('w-24 py-2.5 pl-0 pr-3 align-top', colors.text)}>{(line.unit?.ucode ?? '').trim() || ''}</td>
                        <td className={cn('w-28 py-2.5 pl-0 pr-3 align-top tabular-nums', colors.text)}>{line.requisitionQty ?? ''}</td>
                        <td className={cn('w-20 py-2.5 pl-0 pr-3 align-top tabular-nums', colors.text)}>{line.qty ?? ''}</td>
                        <td className={cn('max-w-[20rem] py-2.5 pl-0 pr-0 align-top', colors.text)}>{line.itemNotes || ''}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </AccordionItem>
        </Accordion>
      </Card>
    </div>
  );
};

export default Detail;
